import type {
  QueryParam,
  QueryParamsWithDefaults,
  SetQueryParams,
} from '@/modules/common/context/QueryParams/models';

import {useCallback, useContext} from 'react';
import {useSearchParams} from 'react-router';

import {QueryParamsContext} from '@/modules/common/context/QueryParams/context';
// @FIXME store schema in context aswell?
import {schema} from '@/modules/common/context/QueryParams/schema';
import {isValidUrlEntry} from '@/modules/common/context/QueryParams/utils/isValidUrlEntry';
import {stringifyWithSchema} from '@/modules/common/context/QueryParams/utils/stringifyWithSchema';

type UseQueryParamsReturnValue = [QueryParamsWithDefaults, SetQueryParams];

export const useQueryParams = (): UseQueryParamsReturnValue => {
  const [, setSearchParams] = useSearchParams();
  const queryParams = useContext(QueryParamsContext);

  if (queryParams === null) {
    throw new Error(
      'useQueryParams() must be used within a <QueryParamsProvider>'
    );
  }

  const setQueryParams = useCallback<SetQueryParams>(
    (init, options) => {
      setSearchParams((previous) => {
        const previousSearchParams = Object.fromEntries(previous.entries());
        const nextQueryParams = stringifyWithSchema({
          queryParams: init(queryParams),
          schema,
        });
        const nextSearchParams = Object.fromEntries(
          Object.entries({
            // keep unknown/other search params
            ...previousSearchParams,
            ...nextQueryParams,
          }).filter(([key, value]) => isValidUrlEntry(key as QueryParam, value))
        );
        return nextSearchParams;
      }, options);
    },
    [queryParams, setSearchParams]
  );

  return [queryParams, setQueryParams];
};
